import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit, Signal, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLinkWithHref } from '@angular/router';
import {
  IonButton,
  IonCol,
  IonGrid,
  IonInput,
  IonInputPasswordToggle,
  IonItem,
  IonList,
  IonRouterLinkWithHref,
  IonRow,
  LoadingController,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoginLogoComponent, ScreensizeService } from '@okcargo-apps/layout-ionic';
import { EMessageLevel, MessagesUiService } from '@okcargo-apps/messages-ui';
import { ConnectivityService } from '@okcargo-apps/shared-infrastructure';
import { matchPasswordsValidator } from '@okcargo-apps/utils';
import { Subscription, catchError, finalize, throwError } from 'rxjs';
import { AccountService } from '../../account';
import { AuthService } from '../../auth';

@Component({
  selector: 'lib-password-recover-confirm',
  templateUrl: './password-recover-confirm.component.html',
  styleUrls: ['./password-recover-confirm.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    IonRouterLinkWithHref,
    RouterLinkWithHref,
    IonItem,
    IonList,
    ReactiveFormsModule,
    IonButton,
    IonInput,
    IonCol,
    IonRow,
    IonGrid,
    IonInputPasswordToggle,
    TranslateModule,
    LoginLogoComponent,
  ],
})
export class PasswordRecoverConfirmComponent implements OnInit, OnDestroy {
  private readonly accountService = inject(AccountService);
  private readonly authService = inject(AuthService);
  private readonly connectivity = inject(ConnectivityService);

  /** A password operation is a command: it cannot be done without connection. */
  protected readonly isOffline = this.connectivity.isOffline;
  private readonly messagesUiService = inject(MessagesUiService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly screensizeService = inject(ScreensizeService);

  @Input()
  public email!: string;

  @Input()
  public key!: string;

  public passwordForm: FormGroup;

  public account = this.accountService.account;
  public isDesktopView: Signal<boolean> = this.screensizeService.isDesktopView;

  public errorMessage = signal<string | null>(null);

  public changePasswordRequestHelperText: string = this.translate.instant('account.change_password_request_helper');
  public changePasswordRequestErrorText: string = this.translate.instant('error.invalid_email_format');
  public keyHelperText: string = this.translate.instant('account.key_helper');
  public keyErrorText: string | null = this.translate.instant('error.mandatory');
  public newPasswordHelperText: string = this.translate.instant('account.new_password_helper');
  public newPasswordErrorText: string | null = this.translate.instant('error.new_password_min_length');
  public newPasswordRepeatHelperText: string | null = null;
  public newPasswordRepeatErrorText: string | null = this.translate.instant('error.new_password_min_length');

  private statusChangesSubscription!: Subscription;

  constructor() {
    this.passwordForm = new FormGroup(
      {
        email: new FormControl('', [Validators.required, Validators.email]),
        key: new FormControl('', [Validators.required]),
        newPassword: new FormControl('', [Validators.required, Validators.minLength(4)]),
        newPasswordRepeat: new FormControl('', [Validators.required, Validators.minLength(4)]),
      },
      {
        validators: matchPasswordsValidator('newPassword', 'newPasswordRepeat'),
      },
    );
  }

  ngOnInit(): void {
    this.passwordForm.patchValue({
      email: this.email,
      key: this.key,
    });
    this.statusChangesSubscription = this.passwordForm.statusChanges.subscribe(() => {
      this.printErrors();
    });
  }

  ngOnDestroy(): void {
    this.statusChangesSubscription.unsubscribe();
  }

  public async modifyPassword(): Promise<void> {
    if (this.isOffline()) return;

    if (!this.passwordForm.valid) {
      this.printErrors(true);
      return;
    }

    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('general.send_data'),
    });
    await loading.present();

    const { email, newPassword, key } = this.passwordForm.value;

    this.authService
      .changePasswordConfirm(email, newPassword, key)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          // console.log('Error detail:', error.error);
          if (error.status === 400) {
            this.errorMessage.set(this.translate.instant('error.old_password_ko'));
          } else {
            this.errorMessage.set(this.translate.instant('error.unknown'));
          }
          return throwError(() => error);
        }),
        finalize(() => loading.dismiss()),
      )
      .subscribe({
        next: () => {
          this.messagesUiService.setMessage({
            objectId: 'account',
            message: [this.translate.instant('account.password_changed')],
            level: EMessageLevel.INFO,
          });
          this.router.navigate(['/login']);
        },
      });
  }

  private printErrors(markAsTouched = false): void {
    const newPasswordControl = this.passwordForm.get('newPassword');
    const newPasswordRepeatControl = this.passwordForm.get('newPasswordRepeat');

    // TODO move messages to validator if it is possible

    if (!newPasswordControl?.errors) {
      this.newPasswordErrorText = null;
    } else if (
      newPasswordControl.errors &&
      Object.keys(newPasswordControl.errors).length === 1 &&
      newPasswordControl.hasError('passwordMismatch')
    ) {
      this.newPasswordErrorText = this.translate.instant('error.passwords_not_equal');
    } else {
      this.newPasswordErrorText = this.translate.instant('error.new_password_min_length');
    }

    if (!newPasswordRepeatControl?.errors) {
      this.newPasswordRepeatErrorText = null;
    } else if (
      newPasswordRepeatControl.errors &&
      Object.keys(newPasswordRepeatControl.errors).length === 1 &&
      newPasswordRepeatControl.hasError('passwordMismatch')
    ) {
      this.newPasswordRepeatErrorText = this.translate.instant('error.passwords_not_equal');
    } else {
      this.newPasswordRepeatErrorText = this.translate.instant('error.new_password_min_length');
    }

    if (this.passwordForm.valid) {
      this.errorMessage.set(null);
    } else if (markAsTouched) {
      this.passwordForm.markAllAsTouched();
      this.errorMessage.set(this.translate.instant('error.form'));
    }
  }
}
